import { createUrl } from "@/redux/api";
import {
  AnswerResponse,
  SurveyResponse,
  QuestionResponse,
  SectionResponse,
  SurveyQueryParams,
  Tags,
  TagsType,
  SectionQueryParams,
  QuestionQueryParams,
  AnswerApiQueryParams,
  SurveyAssignmentResponse,
  SurveyAssignmentApiQueryParams,
  SurveyAssignmentStatisticsAdminResponse,
  SectionStatisticsResponse,
  SectionStatisticsQueryParams,
  QuestionSuggestionResponse,
  QuestionSuggestionStatusResponse,
  SurveySampleResponse,
  SurveyChecklistStateResponse,
  SurveyChecklistRequest,
} from "@/redux/survey/types";
import { isPaginatedResponse } from "@/redux/typeGuards";
import { BuilderType, PaginatedResponse } from "@/redux/types";
import { BaseUserResponse } from "@/redux/users/types";

const surveyService = (builder: BuilderType<Tags>) => ({
  getSurveys: builder.query<
    SurveyResponse[] | PaginatedResponse<SurveyResponse>,
    SurveyQueryParams
  >({
    query: (filters) => createUrl("/surveys/", filters),
    providesTags: (result): TagsType[] =>
      result
        ? [
            ...(
              (isPaginatedResponse(result)
                ? result.results
                : result) as SurveyResponse[]
            ).map(
              (i): TagsType => ({
                type: "Survey",
                id: i.id,
              }),
            ),
            { type: "Surveys" },
          ]
        : [],
  }),
  getSurveyById: builder.query<
    SurveyResponse,
    { surveyId: number | string; filters?: SurveyQueryParams }
  >({
    query: ({ surveyId, filters }) =>
      createUrl(`/surveys/${surveyId}/`, filters),
    providesTags: (result) =>
      result ? [{ type: "Survey", id: result.id }] : [],
  }),
  getSurveySamples: builder.query<SurveySampleResponse[], number>({
    query: (surveyId) => `/surveys/${surveyId}/samples/`,
    providesTags: (result) => (result ? [{ type: "SurveySamples" }] : []),
  }),
  getSections: builder.query<
    SectionResponse[] | PaginatedResponse<SectionResponse>,
    SectionQueryParams
  >({
    query: (filters = {}) => createUrl("/sections/", filters),
    providesTags: (result): TagsType[] =>
      result
        ? [
            ...(
              (isPaginatedResponse(result)
                ? result.results
                : result) as SectionResponse[]
            ).map(
              (i): TagsType => ({
                type: "Section",
                id: i.id,
              }),
            ),
            { type: "Sections" },
          ]
        : [],
  }),
  getSectionById: builder.query<SectionResponse, number>({
    query: (id) => `/sections/${id}/`,
    providesTags: (result) =>
      result ? [{ type: "Section", id: result.id }, { type: "Sections" }] : [],
  }),
  getQuestions: builder.query<
    QuestionResponse[] | PaginatedResponse<QuestionResponse>,
    QuestionQueryParams
  >({
    query: (filters = {}) => createUrl("/questions/", filters),
    providesTags: (result): TagsType[] =>
      result
        ? [
            ...(
              (isPaginatedResponse(result)
                ? result.results
                : result) as QuestionResponse[]
            ).map(
              (i): TagsType => ({
                type: "Question",
                id: i.id,
              }),
            ),
            { type: "Questions" },
          ]
        : [],
  }),
  getQuestionById: builder.query<
    QuestionResponse,
    { questionId: number; filters?: QuestionQueryParams }
  >({
    query: ({ questionId, filters = {} }) =>
      createUrl(`/questions/${questionId}/`, filters),
    providesTags: (result) =>
      result
        ? [{ type: "Question", id: result.id }, { type: "Questions" }]
        : [],
  }),
  getQuestionSuggestions: builder.query<QuestionSuggestionResponse, number>({
    query: (questionId) => `/questions/${questionId}/suggestions/`,
    providesTags: (result, error, questionId) =>
      result ? [{ type: "QuestionSuggestions", id: questionId }] : [],
  }),
  getQuestionSuggestionStatus: builder.query<
    QuestionSuggestionStatusResponse,
    number
  >({
    query: (questionId) => `/questions/${questionId}/suggestion-status/`,
    providesTags: (result, error, questionId) =>
      result ? [{ type: "QuestionSuggestionStatus", id: questionId }] : [],
  }),
  getAnswers: builder.query<
    AnswerResponse[] | PaginatedResponse<AnswerResponse>,
    AnswerApiQueryParams
  >({
    query: (filters) => createUrl("/answers/", filters),
    providesTags: (result): TagsType[] =>
      result
        ? [
            ...(
              (isPaginatedResponse(result)
                ? result.results
                : result) as AnswerResponse[]
            ).map(
              (i): TagsType => ({
                type: "Answer",
                id: i.id,
              }),
            ),
            { type: "Answers" },
          ]
        : [],
  }),
  getAnswerById: builder.query<
    AnswerResponse,
    { id: number; filters: AnswerApiQueryParams }
  >({
    query: ({ id, filters = {} }) => ({
      url: createUrl(`/answers/${id}/`, filters),
      method: "GET",
    }),
    providesTags: (result) =>
      result ? [{ type: "Answer", id: result.id }] : [],
  }),
  createAnswer: builder.mutation<AnswerResponse, any>({
    query: (data) => ({
      url: "/answers/",
      method: "POST",
      data,
    }),
    invalidatesTags: (result) => {
      if (!result) return [];

      const tags: TagsType[] = [
        { type: "SurveyOrganizationStatistics" },
        { type: "QuestionSuggestionStatus", id: result.question },
      ];

      result.attachedBlocks.forEach((block) => {
        tags.push({ type: "DataBlockVersion", id: block.id });
      });

      return tags;
    },
  }),
  updateAnswer: builder.mutation<AnswerResponse, { id: number; data: any }>({
    query: ({ id, data }) => ({
      url: `/answers/${id}/`,
      method: "PUT",
      data,
    }),
    invalidatesTags: (result) => {
      if (!result) return [];

      const tags: TagsType[] = [
        { type: "SurveyOrganizationStatistics" },
        { type: "QuestionSuggestionStatus", id: result.question },
      ];

      result.attachedBlocks.forEach((block) => {
        tags.push({ type: "DataBlockVersion", id: block.id });
      });

      return tags;
    },
  }),
  patchAnswer: builder.mutation<AnswerResponse, { id: number; data: any }>({
    query: ({ id, data }) => ({
      url: `/answers/${id}/`,
      method: "PATCH",
      data,
    }),
    invalidatesTags: (result) => {
      if (!result) return [];

      const tags: TagsType[] = [
        { type: "SurveyOrganizationStatistics" },
        { type: "QuestionSuggestionStatus", id: result.question },
      ];

      result.attachedBlocks.forEach((block) => {
        tags.push({ type: "DataBlockVersion", id: block.id });
      });

      return tags;
    },
  }),
  deleteAnswer: builder.mutation<void, number>({
    query: (id) => ({
      url: `/answers/${id}/`,
      method: "DELETE",
    }),
    invalidatesTags: (result, error) => {
      return error ? [] : [{ type: "SurveyOrganizationStatistics" }];
    },
  }),
  getSurveyAssignments: builder.query<
    SurveyAssignmentResponse[] | PaginatedResponse<SurveyAssignmentResponse>,
    SurveyAssignmentApiQueryParams
  >({
    query: (filters = {}) => createUrl("/surveys/assignments/", filters),
    providesTags: (result) =>
      result
        ? [
            ...(
              (isPaginatedResponse(result)
                ? result.results
                : result) as SurveyAssignmentResponse[]
            ).map(
              (i): TagsType => ({
                type: "SurveyAssignment",
                id: i.id,
              }),
            ),
            { type: "SurveyAssignments" },
          ]
        : [],
  }),
  updateSurveyAssignment: builder.mutation<
    SurveyAssignmentResponse,
    { id: number; data: any }
  >({
    query: ({ id, data }) => ({
      url: `/surveys/assignments/${id}/`,
      method: "PATCH",
      data,
    }),
    invalidatesTags: (result) =>
      result
        ? [
            { type: "SurveyDetails" },
            { type: "Survey", id: result.survey.id },
            { type: "DataBlockVersion" },
          ]
        : [],
  }),
  revertSurveyAssignmentSubmission: builder.mutation<
    SurveyAssignmentStatisticsAdminResponse,
    number
  >({
    query: (id) => ({
      url: `/surveys/assignments/${id}/revert-submission/`,
      method: "POST",
    }),
    invalidatesTags: [
      { type: "SurveyDetails" },
      { type: "SurveyAssignments" },
      { type: "Surveys" },
    ],
  }),
  sendSurveyAssignments: builder.mutation<SurveyAssignmentResponse[], any>({
    query: (data) => ({
      url: "/surveys/assignments/",
      method: "POST",
      data,
    }),
    invalidatesTags: (result) =>
      result
        ? [
            { type: "SurveyAssignments" },
            { type: "Surveys" },
            { type: "SurveyDetails" },
          ]
        : [],
  }),
  getSurveyChecklist: builder.query<SurveyChecklistStateResponse, number>({
    query: (assignmentId) => `/surveys/assignments/${assignmentId}/checklist/`,
    providesTags: (_, __, assignmentId) => [
      { type: "SurveyChecklistState", id: assignmentId },
    ],
  }),
  updateSurveyChecklist: builder.mutation<
    SurveyChecklistStateResponse,
    SurveyChecklistRequest
  >({
    query: ({ assignmentId, data }) => ({
      url: `/surveys/assignments/${assignmentId}/checklist/`,
      method: "PATCH",
      data: { checklistState: data },
    }),
    invalidatesTags: (_, __, { assignmentId }) => [
      { type: "SurveyChecklistState", id: assignmentId },
    ],
  }),
  getAdminSurveyStatistics: builder.query<
    { results: SurveyAssignmentStatisticsAdminResponse[] },
    any
  >({
    query: (filters = {}) =>
      createUrl("/surveys/assignments/", { ...filters, type: "statistics" }),
    providesTags: (result) =>
      result
        ? [
            ...result.results.map(
              (i): TagsType => ({ type: "SurveyDetail", id: i.id }),
            ),
            { type: "SurveyDetails" },
          ]
        : [],
  }),
  getSurveyStatistics: builder.query<
    SectionStatisticsResponse[],
    SectionStatisticsQueryParams
  >({
    query: ({ survey, section = null, organization = null }) =>
      createUrl(`/surveys/${survey}/statistics/organization/`, {
        organization,
        section,
      }),
    providesTags: (result) =>
      result ? [{ type: "SurveyOrganizationStatistics" }] : [],
  }),
  getSurveyContributors: builder.query<BaseUserResponse[], number>({
    query: (surveyId) => `/surveys/${surveyId}/contributors/`,
    providesTags: (result) =>
      result ? result.map((i) => ({ type: "Users", id: i.id })) : [],
  }),
});

export default surveyService;
